/**
 * BW20.2 PR-G — /settings/integrations Garmin Connect section.
 *
 * Surfaces:
 *   - Status chip: "Connected via Edge Function" (green) /
 *     "Connected via Python sidecar" (green) /
 *     "Degraded — using fallback" (amber) /
 *     "Failed — needs attention" (brick) /
 *     "Disconnected" (gray)
 *   - Last successful sync timestamp + last failure (if any) from
 *     garmin_sync_attempt
 *   - Sync now button: POST /api/garmin/sync, refreshes immediately
 *   - Disconnect button: POST /api/garmin/disconnect
 *   - Reset to primary button: POST /api/garmin/reset-primary
 *   - Last 10 attempts table for diagnostics
 *
 * Adherence-neutral copy throughout. No streak counts, no smiley
 * faces, no shame language.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, RefreshCw, X, RotateCcw } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { useToast, SkeletonLoader } from "@/components/system";
import { formatError } from "@/lib/format-error";

const WORKER_URL =
  (import.meta.env.VITE_HAUS_WORKER_URL as string | undefined) ??
  "https://haus-worker.rackljo.workers.dev";

type IntegrationHealthRow = {
  current_strategy: "edge_function" | "python_sidecar";
  status: "healthy" | "degraded" | "failed" | "disconnected";
  last_success_at: string | null;
  consecutive_failures: number;
  next_recovery_probe_at: string | null;
};

type AttemptRow = {
  id: string;
  attempted_at: string;
  strategy: "edge_function" | "python_sidecar";
  status: "success" | "failure" | "partial";
  error_message: string | null;
  rows_pulled: number | null;
  latency_ms: number | null;
};

export function GarminConnectSection() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();
  const toast = useToast();

  const health = useQuery({
    queryKey: ["integration_health", "garmin_connect"],
    enabled: Boolean(userId),
    staleTime: 30_000,
    queryFn: async (): Promise<IntegrationHealthRow | null> => {
      const { data, error } = await supabase
        .from("integration_health")
        .select("current_strategy, status, last_success_at, consecutive_failures, next_recovery_probe_at")
        .eq("integration_name", "garmin_connect")
        .maybeSingle();
      if (error) throw error;
      return (data as IntegrationHealthRow | null) ?? null;
    },
  });

  const attempts = useQuery({
    queryKey: ["garmin_sync_attempts", userId],
    enabled: Boolean(userId),
    staleTime: 30_000,
    queryFn: async (): Promise<AttemptRow[]> => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("garmin_sync_attempt")
        .select("id, attempted_at, strategy, status, error_message, rows_pulled, latency_ms")
        .eq("user_id", userId)
        .order("attempted_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return (data ?? []) as AttemptRow[];
    },
  });

  async function callWorker(path: string): Promise<Response> {
    const { data: { session: s } } = await supabase.auth.getSession();
    if (!s?.access_token) throw new Error("Not signed in");
    return fetch(`${WORKER_URL}${path}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${s.access_token}` },
    });
  }

  const sync = useMutation({
    mutationFn: async () => {
      const resp = await callWorker("/api/garmin/sync");
      if (!resp.ok) throw new Error(`sync failed (${resp.status})`);
      return resp.json();
    },
    onSuccess: () => {
      toast.show("Sync triggered. Refresh in a moment to see the result.");
      qc.invalidateQueries({ queryKey: ["integration_health", "garmin_connect"] });
      qc.invalidateQueries({ queryKey: ["garmin_sync_attempts", userId] });
    },
    onError: (e) => toast.show(`Sync error: ${formatError(e)}`),
  });

  const disconnect = useMutation({
    mutationFn: async () => {
      const resp = await callWorker("/api/garmin/disconnect");
      if (!resp.ok) throw new Error(`disconnect failed (${resp.status})`);
      return resp.json();
    },
    onSuccess: () => {
      toast.show("Garmin disconnected.");
      qc.invalidateQueries({ queryKey: ["integration_health", "garmin_connect"] });
    },
    onError: (e) => toast.show(`Couldn't disconnect: ${formatError(e)}`),
  });

  const resetPrimary = useMutation({
    mutationFn: async () => {
      const resp = await callWorker("/api/garmin/reset-primary");
      if (!resp.ok) throw new Error(`reset failed (${resp.status})`);
      return resp.json();
    },
    onSuccess: () => {
      toast.show("Reset to Edge Function as primary.");
      qc.invalidateQueries({ queryKey: ["integration_health", "garmin_connect"] });
    },
    onError: (e) => toast.show(`Couldn't reset: ${formatError(e)}`),
  });

  if (health.isLoading) return <SkeletonLoader variant="card" height={220} />;

  const row: IntegrationHealthRow | null = health.data ?? null;
  const tone = chipTone(row);

  return (
    <article
      style={{
        padding: "var(--space-4)",
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        display: "grid",
        gap: "var(--space-3)",
      }}
    >
      <header style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
        <Activity size={18} />
        <strong style={{ flex: 1, fontSize: 14 }}>Garmin Connect (direct)</strong>
        <span
          style={{
            fontSize: 12,
            color: tone.color,
            background: tone.bg,
            padding: "4px 10px",
            borderRadius: 999,
            border: `1px solid ${tone.color}`,
          }}
        >
          {tone.label}
        </span>
      </header>

      <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.5 }}>
        {bannerCopy(row)}
      </p>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          style={btnStyle()}
        >
          <RefreshCw size={14} strokeWidth={1.6} />
          {sync.isPending ? "Syncing…" : "Sync now"}
        </button>
        <button
          type="button"
          onClick={() => resetPrimary.mutate()}
          disabled={resetPrimary.isPending}
          style={btnStyle()}
        >
          <RotateCcw size={14} strokeWidth={1.6} />
          Reset to primary
        </button>
        <button
          type="button"
          onClick={() => disconnect.mutate()}
          disabled={disconnect.isPending}
          style={btnStyle("brick")}
        >
          <X size={14} strokeWidth={1.6} />
          Disconnect
        </button>
      </div>

      <details>
        <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--fg-3)" }}>
          Last 10 sync attempts
        </summary>
        <AttemptsTable rows={attempts.data ?? []} loading={attempts.isLoading} />
      </details>
    </article>
  );
}

function chipTone(row: IntegrationHealthRow | null): { label: string; color: string; bg: string } {
  if (!row) {
    return { label: "Not configured", color: "var(--fg-3)", bg: "var(--bg-3)" };
  }
  const strategyLabel =
    row.current_strategy === "edge_function" ? "Edge Function" : "Python sidecar";
  switch (row.status) {
    case "healthy":
      return {
        label: `Connected via ${strategyLabel}`,
        color: "var(--accent)",
        bg: "var(--accent-wash, var(--bg-3))",
      };
    case "degraded":
      return {
        label: "Degraded — using fallback",
        color: "var(--amber, #b07b00)",
        bg: "color-mix(in oklab, var(--amber, #b07b00) 14%, transparent)",
      };
    case "failed":
      return {
        label: "Failed — needs attention",
        color: "var(--brick, #b04a30)",
        bg: "color-mix(in oklab, var(--brick, #b04a30) 14%, transparent)",
      };
    case "disconnected":
      return { label: "Disconnected", color: "var(--fg-3)", bg: "var(--bg-3)" };
  }
}

function bannerCopy(row: IntegrationHealthRow | null): string {
  if (!row) return "Garmin Connect is not configured yet.";
  const lastSuccess = row.last_success_at
    ? `Last success ${formatRelative(row.last_success_at)}.`
    : "No successful sync yet.";
  const failures =
    row.consecutive_failures > 0
      ? ` ${row.consecutive_failures} consecutive failure${row.consecutive_failures === 1 ? "" : "s"} on the current strategy.`
      : "";
  if (row.status === "failed") {
    return `${lastSuccess}${failures} The daily Recovery Score is missing the HRV input until the pull recovers. Tap Sync now to retry, or Reset to primary if the Edge Function is back.`;
  }
  if (row.status === "degraded") {
    return `${lastSuccess}${failures} Currently using the fallback strategy. Recovery probe runs after 7 days without a primary success.`;
  }
  if (row.status === "disconnected") {
    return "Garmin Connect is disconnected. Re-enable from the Worker secrets to resume the daily pull.";
  }
  return `${lastSuccess} Daily pull runs at 06:00 UTC.`;
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const diffMs = Date.now() - t;
  const min = Math.round(diffMs / 60000);
  if (min < 2) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function btnStyle(tone?: "brick"): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    background: "var(--bg-3)",
    color: tone === "brick" ? "var(--brick, #b04a30)" : "var(--fg)",
    border: `1px solid ${tone === "brick" ? "var(--brick, #b04a30)" : "var(--hair)"}`,
    borderRadius: "var(--radius-card)",
    fontSize: 13,
    cursor: "pointer",
  };
}

function AttemptsTable({ rows, loading }: { rows: AttemptRow[]; loading: boolean }) {
  if (loading) {
    return <SkeletonLoader variant="block" count={3} />;
  }
  if (rows.length === 0) {
    return (
      <p style={{ color: "var(--fg-3)", fontSize: 12, marginTop: 8 }}>
        No attempts logged yet.
      </p>
    );
  }
  return (
    <table
      style={{
        width: "100%",
        marginTop: 8,
        fontSize: 12,
        borderCollapse: "collapse",
        color: "var(--fg-2)",
      }}
    >
      <thead style={{ textAlign: "left" }}>
        <tr style={{ borderBottom: "1px solid var(--hair)" }}>
          <th style={{ padding: "6px 4px", fontWeight: 500, color: "var(--fg-3)" }}>When</th>
          <th style={{ padding: "6px 4px", fontWeight: 500, color: "var(--fg-3)" }}>Strategy</th>
          <th style={{ padding: "6px 4px", fontWeight: 500, color: "var(--fg-3)" }}>Status</th>
          <th style={{ padding: "6px 4px", fontWeight: 500, color: "var(--fg-3)" }}>Latency</th>
          <th style={{ padding: "6px 4px", fontWeight: 500, color: "var(--fg-3)" }}>Error</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} style={{ borderBottom: "1px solid var(--hair)" }}>
            <td style={{ padding: "6px 4px" }}>{formatRelative(r.attempted_at)}</td>
            <td style={{ padding: "6px 4px" }}>
              {r.strategy === "edge_function" ? "Edge Function" : "Python sidecar"}
            </td>
            <td
              style={{
                padding: "6px 4px",
                color:
                  r.status === "success"
                    ? "var(--accent)"
                    : r.status === "partial"
                    ? "var(--amber, #b07b00)"
                    : "var(--brick, #b04a30)",
              }}
            >
              {r.status}
            </td>
            <td style={{ padding: "6px 4px" }}>
              {r.latency_ms != null ? `${r.latency_ms} ms` : "—"}
            </td>
            <td
              style={{
                padding: "6px 4px",
                maxWidth: 260,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={r.error_message ?? undefined}
            >
              {r.error_message ?? "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
